import React from 'react';
import { Card, CardContent } from '../ui/Card';
import Badge from '../ui/Badge';
import Input from '../ui/Input';
import EnableToggle from '../ui/EnableToggle';
import BasketNameLink from '../basket/BasketNameLink';
import { isWhiteLabel } from '../../config/tenantConfig';
import { ALLOCATION_STATUS, ALLOCATION_SOURCE } from '../../constants/tradingConstants';
import {
  Network, ExternalLink, Save, X, RefreshCw, Trash2, Pencil
} from 'lucide-react';

interface EnhancedAllocation {
  allocation_id: string;
  basket_id: string;
  basket_name: string;
  broker_id: string;
  client_id: string;
  lot_multiplier: number;
  status: 'ACTIVE' | 'INACTIVE';
  created_at: string;
  updated_at: string;
  // 'MARKETPLACE' on subscriber rows whose basket is owned by an admin/publisher.
  source?: string;
}

interface AllocationCardProps {
  allocation: EnhancedAllocation;
  isSelected: boolean;
  onSelect: (allocationId: string) => void;
  onStatusToggle: (allocation: EnhancedAllocation) => void;
  onDelete: (allocation: EnhancedAllocation) => void;
  onStartEdit: (allocation: EnhancedAllocation) => void;
  onSaveEdit: (allocation: EnhancedAllocation) => void;
  onCancelEdit: () => void;
  isEditing: boolean;
  isUpdating: boolean;
  editLotMultiplier: number;
  onEditLotMultiplierChange: (value: number) => void;
}

const AllocationCard: React.FC<AllocationCardProps> = ({
  allocation,
  isSelected,
  onSelect,
  onStatusToggle,
  onDelete,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  isEditing,
  isUpdating,
  editLotMultiplier,
  onEditLotMultiplierChange
}) => {
  const wl = isWhiteLabel();
  const isMarketplace = allocation.source === ALLOCATION_SOURCE.MARKETPLACE;
  return (
    <Card className={`transition-all hover:shadow-md ${
      isSelected ? `ring-2 ${wl ? 'ring-zebu-500 bg-zebu-50 dark:bg-zebu-900/10' : 'ring-blue-500 bg-blue-50 dark:bg-blue-900/10'}` : ''
    }`}>
      <CardContent className="p-4">
        {/* Header with selection and status */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onSelect(allocation.allocation_id)}
              className={`rounded border-gray-300 ${wl ? 'text-zebu-600 focus:ring-zebu-500' : 'text-blue-600 focus:ring-blue-500'}`}
            />
            <div className="flex items-center gap-2 min-w-0">
              <Network
                className={`h-4 w-4 shrink-0 ${
                  isMarketplace
                    ? 'text-violet-500 dark:text-violet-400'
                    : wl ? 'text-zebu-500' : 'text-blue-500'
                }`}
              />
              <BasketNameLink
                basketId={allocation.basket_id}
                basketName={allocation.basket_name}
                isMarketplace={isMarketplace}
                size="md"
              />
            </div>
          </div>
          <EnableToggle
            enabled={allocation.status === ALLOCATION_STATUS.ACTIVE}
            loading={isUpdating}
            onChange={() => onStatusToggle(allocation)}
            size="md"
          />
        </div>

        {/* Basket ID — external link to /baskets is suppressed for marketplace
            rows because the basket lives under the publisher's partition and
            isn't present in the subscriber's BasketsPage. */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Basket ID: {allocation.basket_id}
          </span>
          {!isMarketplace && (
            <button
              onClick={() => window.open(`/baskets`, '_blank')}
              className={`text-gray-400 ${wl ? 'hover:text-zebu-600' : 'hover:text-blue-600'} transition-colors`}
              title="View basket details"
            >
              <ExternalLink className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Main allocation details */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">Broker:</span>
            <Badge variant="info" size="sm">{allocation.broker_id}</Badge>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">Client ID:</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {allocation.client_id}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">Lot Multiplier:</span>
            <div className="flex items-center gap-1.5">
              {isEditing ? (
                <>
                  <Input
                    type="number"
                    min="1"
                    max="250"
                    value={editLotMultiplier}
                    onChange={(e) => onEditLotMultiplierChange(parseInt(e.target.value) || 1)}
                    className="w-20 text-sm h-8"
                  />
                  <button
                    onClick={() => onSaveEdit(allocation)}
                    disabled={isUpdating}
                    className="text-green-600 hover:text-green-700 disabled:opacity-50 p-1"
                    title="Save"
                  >
                    {isUpdating ? (
                      <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Save className="h-3.5 w-3.5" />
                    )}
                  </button>
                  <button
                    onClick={onCancelEdit}
                    className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 p-1"
                    title="Cancel"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </>
              ) : (
                <>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {allocation.lot_multiplier}
                  </span>
                  <button
                    onClick={() => onStartEdit(allocation)}
                    disabled={isUpdating}
                    className={`p-0.5 rounded text-gray-400 ${wl ? 'hover:text-zebu-600' : 'hover:text-blue-600'} transition-colors disabled:opacity-30`}
                    title="Edit lot multiplier"
                    aria-label="Edit lot multiplier"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">Created:</span>
            <span className="text-sm text-gray-900 dark:text-white">
              {new Date(allocation.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => onDelete(allocation)}
            disabled={isUpdating || isEditing}
            className="p-2 rounded text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
            title="Delete allocation"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AllocationCard;